import { useState, useEffect, useCallback } from 'react'
import { createPortal } from 'react-dom'
import { X, Target, Edit2, Trash2, Rocket, CheckCircle } from 'lucide-react'
import { useCloserLeadManager } from '@/hooks/useCloserLeadManager'
import { getQrLeadScanHints } from '@/utils/qrLeadScanFlow'
import { Button } from '@/components/ui/Button'
import CloserLeadForm from './CloserLeadForm'
import CloserLeadDetails from './CloserLeadDetails'
import type { CloserLead, CloserLeadFormData } from '@/types'

interface CloserLeadManagerModalProps {
  open: boolean
  onClose: () => void
  mode: 'create' | 'edit' | 'view' | 'discard'
  lead?: CloserLead | null
  onSuccess: () => void
  onDiscardLead: (id: string, status: string, motivo: string) => Promise<void>
  onRequestConvertToDeal?: (lead: CloserLead) => void
  initialPrefill?: Partial<CloserLeadFormData>
  highlightEmpresa?: boolean
  qrSource?: import('@/utils/parseContactQrPayload').ParsedContactQr['source']
}

export function CloserLeadManagerModal({
  open,
  onClose: externalClose,
  mode: initialMode,
  lead,
  onSuccess,
  onDiscardLead,
  onRequestConvertToDeal,
  initialPrefill,
  highlightEmpresa: highlightEmpresaProp = false,
  qrSource,
}: CloserLeadManagerModalProps) {
  const manager = useCloserLeadManager(onSuccess, externalClose)
  const [showRegForm, setShowRegForm] = useState(false)
  const [showDiscardForm, setShowDiscardForm] = useState(false)
  const [regCanal, setRegCanal] = useState('Telefone')
  const [regResultado, setRegResultado] = useState('')
  const [regObs, setRegObs] = useState('')
  const [newPendingText, setNewPendingText] = useState('')

  useEffect(() => {
    if (!open) {
      manager.handleCloseModal({ skipDraftSave: true })
      setShowDiscardForm(false)
      return
    }

    if (initialMode === 'create') {
      if (initialPrefill) {
        manager.openCreateWithPrefill(initialPrefill, {
          ...(highlightEmpresaProp ? { highlightEmpresa: true } : {}),
          qrSource,
        })
      } else {
        manager.openCreate()
      }
    } else if (lead) {
      manager.openDetails(lead)
      setShowDiscardForm(initialMode === 'discard')
    }
  }, [open, initialMode, lead?.id, initialPrefill, highlightEmpresaProp, qrSource])

  const handleClose = useCallback(() => {
    manager.handleCloseModal()
  }, [manager])

  useEffect(() => {
    if (!open) return

    document.body.style.overflow = 'hidden'
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') handleClose()
    }
    document.addEventListener('keydown', handleEscape)
    return () => {
      document.body.style.overflow = ''
      document.removeEventListener('keydown', handleEscape)
    }
  }, [open, handleClose])

  const handleSave = useCallback(async () => {
    if (manager.isNewLead) {
      await manager.handleAddLead(manager.formData)
    } else if (manager.selectedLead) {
      await manager.handleUpdateLead(manager.selectedLead.id, manager.formData)
    }
  }, [manager])

  const handleDelete = useCallback(async () => {
    if (!manager.selectedLead) return
    await manager.handleDeleteLead(manager.selectedLead.id)
  }, [manager])

  const handleConvert = useCallback(async () => {
    if (!manager.selectedLead) return
    if (onRequestConvertToDeal) {
      onRequestConvertToDeal(manager.selectedLead)
      return
    }
    await manager.handleConvertToDeal(manager.selectedLead.id)
  }, [manager, onRequestConvertToDeal])

  const handleDiscard = useCallback(async (status: string, motivo: string) => {
    if (!manager.selectedLead) return
    await onDiscardLead(manager.selectedLead.id, status, motivo)
    handleClose()
  }, [manager, onDiscardLead, handleClose])

  const handleRegisterContact = useCallback(async () => {
    if (!manager.selectedLead || !regResultado) return
    await manager.handleRegTentativa(manager.selectedLead.id, {
      canal: regCanal,
      resultado: regResultado,
      obs: regObs,
    })
    setShowRegForm(false)
    setRegResultado('')
    setRegObs('')
  }, [manager, regCanal, regResultado, regObs])

  const handleAddPending = useCallback(async () => {
    if (!manager.selectedLead || !newPendingText.trim()) return
    await manager.handleAddPending(manager.selectedLead.id, newPendingText)
    setNewPendingText('')
  }, [manager, newPendingText])

  const handleTogglePending = useCallback(async (pendingId: number) => {
    if (!manager.selectedLead) return
    await manager.togglePending(manager.selectedLead.id, pendingId)
  }, [manager])

  const handleRemovePending = useCallback(async (pendingId: number) => {
    if (!manager.selectedLead) return
    await manager.removePending(manager.selectedLead.id, pendingId)
  }, [manager])

  if (!open) return null

  const getTitle = () => {
    if (manager.isEditing && manager.isNewLead) return 'Novo Lead'
    if (manager.isEditing) return 'Editar Lead'
    return 'Detalhes do Lead'
  }

  return createPortal(
    <div
      className='fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center p-2 sm:p-6'
      style={{ zIndex: 99999 }}
      onClick={handleClose}
    >
      <div
        className='relative z-10 w-full max-w-5xl max-h-[95vh] sm:max-h-[90vh] h-[95vh] sm:h-[90vh] rounded-2xl flex flex-col shadow-2xl border border-[color:var(--border)] bg-[var(--bg-card)] min-h-0'
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div className='flex items-center justify-between p-4 sm:p-6 border-b border-[color:var(--border)] bg-[var(--bg-main)] shrink-0'>
          <div className='flex items-center gap-3'>
            <div className='p-2 rounded-xl bg-accent/10 border border-accent/20 text-accent'>
              <Target size={18} />
            </div>
            <h2 className='text-base sm:text-xl font-bold text-[var(--text-primary)] uppercase tracking-tight'>
              {getTitle()}
            </h2>
          </div>
          <button
            onClick={handleClose}
            className='p-2 rounded-lg text-[var(--text-muted)] hover:text-[var(--text-primary)] hover:bg-[var(--glass-bg)] transition-all border border-[color:var(--border)]'
          >
            <X size={18} />
          </button>
        </div>

        {/* Body */}
        <div className='flex-1 min-h-0 overflow-y-auto p-4 sm:p-6 bg-[var(--bg-main)]'>
          {manager.isNewLead && manager.isEditing && manager.hasDraft && (
            <div className='mb-3 flex justify-end'>
              <button
                type='button'
                onClick={manager.clearDraft}
                className='text-[10px] font-bold uppercase tracking-wider text-text-muted hover:text-red-400 transition-colors'
              >
                Limpar rascunho
              </button>
            </div>
          )}
          {!manager.isEditing && manager.selectedLead ? (
            <CloserLeadDetails
              lead={manager.selectedLead}
              onConvertToDeal={handleConvert}
              onDiscardLead={handleDiscard}
              onRegisterContact={handleRegisterContact}
              showRegForm={showRegForm}
              setShowRegForm={setShowRegForm}
              showDiscardForm={showDiscardForm}
              setShowDiscardForm={setShowDiscardForm}
              regCanal={regCanal}
              setRegCanal={setRegCanal}
              regResultado={regResultado}
              setRegResultado={setRegResultado}
              regObs={regObs}
              setRegObs={setRegObs}
              onRegisterSubmit={handleRegisterContact}
              newPendingText={newPendingText}
              setNewPendingText={setNewPendingText}
              onAddPending={handleAddPending}
              onTogglePending={handleTogglePending}
              onRemovePending={handleRemovePending}
            />
          ) : (
            <CloserLeadForm
              formData={manager.formData}
              setFormData={manager.setFormData}
              mode={manager.isNewLead ? 'create' : 'edit'}
              backendErrors={manager.backendErrors}
              highlightEmpresa={manager.highlightEmpresa || highlightEmpresaProp}
              highlightContato={manager.highlightContato}
              qrFromWhatsApp={manager.qrFromWhatsApp}
              onEmpresaFilled={() => manager.setHighlightEmpresa(false)}
              onContatoFilled={() => manager.setHighlightContato(false)}
              onAfterQrScan={(result) => {
                const hints = getQrLeadScanHints(result)
                manager.setHighlightEmpresa(hints.highlightEmpresa)
                manager.setHighlightContato(hints.highlightContato)
                manager.setQrFromWhatsApp(hints.whatsappOnlyPhone)
              }}
            />
          )}
        </div>

        {/* Footer */}
        <div className='p-4 sm:p-6 border-t border-[color:var(--border)] bg-[var(--bg-main)] flex items-center justify-between shrink-0'>
          <div>
            {!manager.isEditing && manager.selectedLead && (
              <Button
                variant='destructive'
                size='sm'
                icon={<Trash2 size={14} />}
                onClick={handleDelete}
              >
                Excluir
              </Button>
            )}
          </div>
          <div className='flex gap-3'>
            {!manager.isEditing && manager.selectedLead ? (
              <>
                <Button
                  variant='secondary'
                  size='sm'
                  icon={<Edit2 size={14} />}
                  onClick={() => manager.handleStartEdit()}
                >
                  Editar
                </Button>
                <Button
                  variant='primary'
                  size='sm'
                  icon={<Rocket size={14} />}
                  onClick={handleConvert}
                >
                  Converter em Deal
                </Button>
              </>
            ) : (
              <>
                {!manager.isNewLead && (
                  <Button variant='ghost' size='sm' onClick={() => manager.handleCancelEdit()}>
                    Cancelar
                  </Button>
                )}
                <Button
                  variant='primary'
                  size='sm'
                  icon={<CheckCircle size={14} />}
                  loading={manager.saving}
                  onClick={handleSave}
                >
                  {manager.isNewLead ? 'Cadastrar' : 'Salvar Alterações'}
                </Button>
              </>
            )}
          </div>
        </div>
      </div>
    </div>,
    document.body
  )
}

export default CloserLeadManagerModal
